import * as React from 'react'

import { cn } from '../cn'
import { SIDEBAR_TILE_COLORS } from './app-sidebar'
import { Badge } from './badge'

/**
 * Dietary / allergen tag chips — THE shared look for food-tag chips across
 * every Ordo app (menus builder, ordo ordering, chefs, …), so "Vegetarian" is
 * the same green pill everywhere and allergen "Contains" chips read the same
 * on every surface.
 *
 * - Dietary (filterable) tags render as Ordo light-badge pills, colored per
 *   claim via {@link dietaryTagColorKey}.
 * - Allergen (hazard) chips stay NEUTRAL (`secondary`) — allergens are
 *   nutrition metadata, not badges of honor; an `emphasized` variant exists
 *   for safety-critical call-outs (e.g. a student's own restricted allergen).
 */

export type OrdoLightColorKey =
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | 'slate'
  | 'gray'

/** Badge classes for an Ordo light tile color (soft bg + colored text). */
export function ordoLightBadgeClassName(colorKey: OrdoLightColorKey, className?: string): string {
  return cn('border-transparent shadow-none', SIDEBAR_TILE_COLORS[colorKey] ?? SIDEBAR_TILE_COLORS.gray, className)
}

// Ordo light-badge color per canonical dietary claim — anchored to the colors
// these concepts already carry across Ordo surfaces: green Vegetarian "V"
// badge and blue Dairy-Free "DF" badge on the parent menu preview, green VEG
// bucket in the menu taxonomy, amber = the grain/wheat family for Gluten-Free.
export const DIETARY_TAG_COLOR_KEYS: Readonly<Record<string, OrdoLightColorKey>> = Object.freeze({
  vegetarian: 'green',
  vegan: 'emerald',
  halal: 'teal',
  kosher: 'indigo',
  'gluten-free': 'amber',
  'dairy-free': 'blue',
})

/** Normalize a tag slug OR display name to the mapping key ("Gluten-Free" → "gluten-free"). */
function dietaryTagKey(slugOrName: string): string {
  return slugOrName
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
}

/**
 * Ordo light-badge color for a dietary tag: pinned claim colors, cyan for
 * other "X-free" absence claims, violet for custom dietary tags. Accepts a
 * canonical slug or a display name — callers with legacy slug aliases (e.g.
 * menus' `diet-*` rows) should canonicalize before calling.
 */
export function dietaryTagColorKey(slugOrName: string): OrdoLightColorKey {
  const key = dietaryTagKey(slugOrName)
  const pinned = DIETARY_TAG_COLOR_KEYS[key]
  if (pinned) {
    return pinned
  }
  return /(^|-)free$/.test(key) ? 'cyan' : 'violet'
}

export interface DietaryTagChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visible chip text (display name, e.g. "Vegetarian"). */
  label: string
  /** Value the color derives from when it differs from the label (e.g. a canonical slug). Defaults to `label`. */
  colorSource?: string
}

/** A dietary (filterable) tag as its canonical colored light-badge pill. */
export function DietaryTagChip({ label, colorSource, className, ...props }: DietaryTagChipProps) {
  return (
    <Badge
      variant="outline"
      className={ordoLightBadgeClassName(dietaryTagColorKey(colorSource ?? label), className)}
      {...props}>
      {label}
    </Badge>
  )
}

export interface AllergenTagChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Safety-critical emphasis (e.g. the tag matches the student's own restricted allergens). */
  emphasized?: boolean
}

/** An allergen (hazard) chip: neutral `secondary`, `destructive` when emphasized. */
export function AllergenTagChip({ emphasized = false, className, children, ...props }: AllergenTagChipProps) {
  return (
    <Badge variant={emphasized ? 'destructive' : 'secondary'} className={className} {...props}>
      {children}
    </Badge>
  )
}
